import { useMemo, useState } from "react";
import { Home, Inbox, KanbanSquare, Loader2, Search, Send, UserRound } from "lucide-react";
import { CrmHome } from "./CrmHome";
import { CrmInbox } from "./CrmInbox";
import { CrmKanban } from "./CrmKanban";
import { CrmCampanhas } from "./CrmCampanhas";
import { useAssumirConversa, useCrmConversas } from "@/hooks/useCrm";
import { FILA_ESTAGIOS, type CrmView } from "./crmUtils";
import { CRM_DEMO_VISUAL } from "./crmMockData";
import type { CrmEstagio } from "@/types/crm";

const VIEWS: { id: CrmView; label: string; icon: typeof Home }[] = [
  { id: "home", label: "Home", icon: Home },
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "kanban", label: "Kanban", icon: KanbanSquare },
  { id: "campanhas", label: "Campanhas", icon: Send },
];

const TITULOS: Record<CrmView, { title: string; subtitle: string }> = {
  home: { title: "Home", subtitle: "Visão do atendimento WhatsApp e do funil comercial." },
  inbox: { title: "Inbox", subtitle: "Conversas em tempo real com handoff entre IA e humano." },
  kanban: { title: "Kanban", subtitle: "Arraste os cards para avançar o estágio do funil." },
  campanhas: { title: "Campanhas", subtitle: "Listas de distribuição segmentadas da base de clientes." },
};

export function CrmModule() {
  const [view, setView] = useState<CrmView>("home");
  const [conversaId, setConversaId] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [buscaDraft, setBuscaDraft] = useState("");
  const [apenasMinhas, setApenasMinhas] = useState(false);
  const [destaqueEstagio, setDestaqueEstagio] = useState<CrmEstagio | null>(null);
  const [novaCampanhaOpen, setNovaCampanhaOpen] = useState(false);
  const assumir = useAssumirConversa();
  const { data: conversas = [] } = useCrmConversas();

  const proximaFila = useMemo(
    () => conversas.find((c) => FILA_ESTAGIOS.includes(c.estagio)) ?? null,
    [conversas]
  );

  const navigate = (next: CrmView, opts?: { conversaId?: string; minhas?: boolean; estagio?: CrmEstagio }) => {
    if (opts?.conversaId) {
      setConversaId(opts.conversaId);
      setApenasMinhas(false);
      setBusca("");
      setBuscaDraft("");
    }
    if (opts?.minhas !== undefined) setApenasMinhas(opts.minhas);
    setDestaqueEstagio(opts?.estagio ?? null);
    setView(next);
  };

  const submitBusca = () => {
    setBusca(buscaDraft.trim());
    setView("inbox");
  };

  const handleAssumirProxima = () => {
    if (!proximaFila) {
      navigate("inbox");
      return;
    }
    assumir.mutate(proximaFila.id, {
      onSuccess: () => navigate("inbox", { conversaId: proximaFila.id }),
    });
  };

  const { title, subtitle } = TITULOS[view];

  return (
    <div className="crm-fynix -m-6 min-h-[calc(100vh-5.5rem)] p-6">
      <header className="flex flex-col xl:flex-row xl:items-center gap-4 mb-5">
        <div className="min-w-[220px]">
          <h1 className="crm-title text-2xl text-fynix-ink">{title}</h1>
          <p className="text-sm text-fynix-muted">
            {subtitle}
            {CRM_DEMO_VISUAL && (
              <span className="ml-2 inline-flex items-center rounded-full bg-fynix-brand-soft px-2 py-0.5 text-[11px] font-medium text-fynix-brand">
                Dados demo
              </span>
            )}
          </p>
        </div>

        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-fynix-muted" />
          <input
            value={buscaDraft}
            onChange={(e) => setBuscaDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitBusca();
            }}
            placeholder="Buscar conversa por nome ou telefone"
            className="w-full h-12 rounded-full border border-fynix-line bg-white pl-11 pr-16 text-sm outline-none focus:ring-2 focus:ring-fynix-brand/30"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] text-fynix-muted">Enter</span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleAssumirProxima}
            disabled={assumir.isPending}
            className="h-11 px-4 rounded-2xl border border-fynix-line bg-white text-sm font-medium hover:bg-fynix-brand-soft disabled:opacity-60 flex items-center gap-2"
          >
            {assumir.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserRound className="h-4 w-4" />}
            Assumir próxima
          </button>
          <button
            type="button"
            onClick={() => {
              setNovaCampanhaOpen(true);
              setView("campanhas");
            }}
            className="h-11 px-4 text-sm flex items-center gap-2 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
          >
            <Send className="h-4 w-4" /> Nova campanha
          </button>
        </div>
      </header>

      <nav className="flex flex-wrap gap-2 mb-5">
        {VIEWS.map((item) => {
          const Icon = item.icon;
          const active = view === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setView(item.id)}
              className={`h-10 px-4 rounded-2xl text-sm font-medium flex items-center gap-2 border transition-colors ${
                active
                  ? "bg-fynix-surface-panel border-fynix-line text-fynix-brand"
                  : "bg-white border-fynix-line text-fynix-ink-soft hover:bg-fynix-brand-soft"
              }`}
            >
              <Icon className={`h-4 w-4 ${active ? "text-fynix-brand" : ""}`} />
              {item.label}
            </button>
          );
        })}
      </nav>

      {view === "home" && (
        <CrmHome
          onNavigate={navigate}
          onNovaCampanha={() => {
            setNovaCampanhaOpen(true);
            setView("campanhas");
          }}
        />
      )}
      {view === "inbox" && (
        <CrmInbox
          conversaId={conversaId}
          onConversaChange={setConversaId}
          busca={busca}
          onBuscaChange={(v) => {
            setBusca(v);
            setBuscaDraft(v);
          }}
          apenasMinhas={apenasMinhas}
          onApenasMinhasChange={setApenasMinhas}
        />
      )}
      {view === "kanban" && (
        <CrmKanban
          busca={busca}
          apenasMinhas={apenasMinhas}
          onBuscaChange={(v) => {
            setBusca(v);
            setBuscaDraft(v);
          }}
          onApenasMinhasChange={setApenasMinhas}
          destaqueEstagio={destaqueEstagio}
          onOpenConversa={(id) => navigate("inbox", { conversaId: id })}
        />
      )}
      {view === "campanhas" && (
        <CrmCampanhas novaOpen={novaCampanhaOpen} onNovaOpenChange={setNovaCampanhaOpen} />
      )}
    </div>
  );
}
